
import { useCallback, useEffect, useState } from "react";
import { getBessLoadGenPeakHours } from "../../../../../Rest_api/RestApi";
import loader from "../../../../../components/recharts/loader.gif";
import { ReactComponent as InfoIcon } from "../../../../../icons/info-Simulator.svg";
import PortfolioPiechart from "./PortfolioPiechart";

interface PeriodData {
    slot_range: string;
    avg_generation: number;
    avg_load: number;
}

interface GenerationData {
    solar: number;
    wind: number;
    hydro: number;
    thermal: number;
    others: number;
}

interface ApiResponse {
    session: string;
    session_id: number;
    scenario: number;
    start_date: string;
    end_date: string;
    period_data: {
        solar_hours: PeriodData;
        morning: PeriodData;
        evening: PeriodData;
    };
    generation_data: GenerationData;
}

export default function LoadGenerationContributor({
    refreshKey,
    selectedBlock,
    startDate,
    endDate,
    scenarioId,
    setError,
}: {
    refreshKey: number;
    selectedBlock: any[];
    startDate: string;
    endDate?: string;
    scenarioId: number;
    setError?: any;
}) {
    const [data, setData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFetchData = useCallback(async () => {
        const effectiveEndDate = endDate || startDate;


        if (!startDate || scenarioId === undefined || scenarioId === null) {
            console.warn("LoadGenerationContributor: Missing required props", { startDate, scenarioId });
            return;
        }

        try {
            setLoading(true);


            const res = await getBessLoadGenPeakHours({
                start_date: startDate,
                end_date: effectiveEndDate,
                scenario_id: scenarioId.toString(),
            });



            if (res) {
                setData(res);
            } else {
                console.warn("LoadGenerationContributor: API returned null or undefined");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            if (setError) setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    }, [scenarioId, startDate, endDate, setError, refreshKey]);

    useEffect(() => {
        handleFetchData();
    }, [handleFetchData]);

    const periodItems = data
        ? [
            {
                name: "Solar Hours",
                generation: data.period_data.solar_hours.avg_generation,
                load: data.period_data.solar_hours.avg_load,
            },
            {
                name: "Morning Hours",
                generation: data.period_data.morning.avg_generation,
                load: data.period_data.morning.avg_load,
            },
            {
                name: "Evening Hours",
                generation: data.period_data.evening.avg_generation,
                load: data.period_data.evening.avg_load,
            },
        ]
        : [];

    const generationItems = data
        ? [
            { name: "Solar", value: data.generation_data.solar },
            { name: "Wind", value: data.generation_data.wind },
            { name: "Hydro", value: data.generation_data.hydro },
            { name: "Thermal", value: data.generation_data.thermal },
            { name: "Others", value: data.generation_data.others },
        ].sort((a, b) => b.value - a.value)
        : [];

    const maxPeriodValue = Math.max(
        ...periodItems.map((d) => Math.max(d.generation, d.load)),
        0
    ) || 1;
    const maxGenerationValue = Math.max(...generationItems.map((d) => d.value), 0) || 1;

    return (
        <div className="col-span-1 md:col-span-12 flex flex-col md:flex-row gap-2 h-full w-full">
            {/* Left Card: Demand-Supply Balance */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full rounded-2xl overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-3 border-b-2 border-[#DFEAF2] pb-1">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Time-of-Day Demand-Supply Balance
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-5 h-5 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1">
                        <div className="space-y-5 w-full h-full overflow-y-auto pr-2 custom-scrollbar">
                            {periodItems.map((item, index) => {
                                const genWidthPercent = (item.generation / maxPeriodValue) * 100;
                                const loadWidthPercent = (item.load / maxPeriodValue) * 100;

                                const isGenZero = item.generation < 0.01;
                                const isLoadZero = item.load < 0.01;

                                const effectiveGenWidth = isGenZero ? 0 : genWidthPercent;
                                const effectiveLoadWidth = isLoadZero ? 0 : loadWidthPercent;

                                const isGenSmall = effectiveGenWidth < 30;
                                const isLoadSmall = effectiveLoadWidth < 30;

                                return (
                                    <div key={index} className="flex flex-col mb-1">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-xs text-[#525062] font-medium w-40 truncate" title={item.name}>
                                                    {item.name}
                                                </span>
                                                <span className="text-[9px] text-[#8C8A96]">
                                                    {item.name === "Solar Hours" ? "(10:00 - 18:00)" :
                                                        item.name === "Morning Hours" ? "(06:00 - 10:00)" :
                                                            "(18:00 - 22:00)"}
                                                </span>
                                            </div>
                                            <div className="flex-1 ml-4">
                                                {/* Gen Bar - Orange/Peach */}
                                                <div className="relative rounded-lg h-6 overflow-hidden mb-1">
                                                    <div
                                                        className={`absolute top-0 left-0 h-6 bg-[#FCE9DE] flex items-center transition-all duration-500 rounded-r-lg ${isGenSmall ? "justify-start pl-4" : "justify-end pr-4"}`}
                                                        style={{ width: `${effectiveGenWidth}%` }}
                                                    >
                                                        {!isGenSmall && (
                                                            <span className="text-[#913A08] font-medium text-[10px]">
                                                                Gen: {item.generation.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isGenSmall && (
                                                        <span
                                                            className="absolute top-0 left-0 h-6 flex items-center text-[#913A08] font-medium text-[10px] ml-4"
                                                            style={{ left: `${effectiveGenWidth}%` }}
                                                        >
                                                            Gen: {item.generation.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Load Bar - Teal */}
                                                <div className="relative rounded-lg h-6 overflow-hidden">
                                                    <div
                                                        className={`absolute top-0 left-0 h-6 bg-[#E3F1F3] flex items-center transition-all duration-500 rounded-r-lg ${isLoadSmall ? "justify-start pl-4" : "justify-end pr-4"}`}
                                                        style={{ width: `${effectiveLoadWidth}%` }}
                                                    >
                                                        {!isLoadSmall && (
                                                            <span className="text-[#34656D] font-medium text-[10px]">
                                                                Load: {item.load.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                            </span>
                                                        )}
                                                    </div>
                                                    {isLoadSmall && (
                                                        <span
                                                            className="absolute top-0 left-0 h-6 flex items-center text-[#34656D] font-medium text-[10px] ml-4"
                                                            style={{ left: `${effectiveLoadWidth}%` }}
                                                        >
                                                            Load: {item.load.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Card: Generation Portfolio */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full min-h-0 rounded-2xl p-3 overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-1 border-b-2 border-[#DFEAF2] pb-1">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Generation Portfolio
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-5 h-5 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1 flex items-center justify-center">
                        {data && <PortfolioPiechart data={[data.generation_data]} chartHeight="100%" />}
                    </div>
                )}
            </div>
        </div>
    );
}
